import * as React from "react"
import { type ApolloClient, useApolloClient } from "@apollo/client"
import {
  type Category,
  type EntityCategory,
  type EntityParty,
  LedgerType,
  PartyDesignation,
  ReadAssociatedThreadsDocument,
  useCloseWorkItemsMutation,
  usePushDigitsWorkItemsMutation,
  usePushWorkItemsMutation,
  useReopenWorkItemMutation,
  type WorkItem,
  WorkItemPushFieldType,
  type WorkItemPushParams,
  WorkItemPushState,
  WorkItemSupportType,
  type WorkItemTransaction,
} from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import { TRANSACTION_REVIEW_TYPES } from "src/frontend/components/OS/Applications/Boost/WorkItems/hooks/useListTransactionReviewItemsQuery"
import { WORK_ITEM_STATE_CHANGE_REFETCH } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/reducers/shared"
import { getSupportingTransactions } from "src/frontend/components/OS/Shared/WorkItems/useSupportingTransactions"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { REFETCH_ACTION_ITEMS } from "src/shared/components/Comments/threadMutationHooks"
import {
  isTypeaheadNewParty,
  NEW_ENTITY_ID,
  type NewPartyEntity,
  type TypeaheadEntity,
} from "src/shared/components/Typeahead/sharedTypeahead"
import customKeyframes from "src/shared/config/customKeyframes"
import { isCategory } from "src/shared/helpers/hierarchicalCategoryHelper"
import { useAnimationCallback } from "src/shared/hooks/useAnimationCallback"

export const NEW_PARTY_ID = "transaction-review-new-party-id"
const REFETCH_ON_CLOSE = [ReadAssociatedThreadsDocument, ...REFETCH_ACTION_ITEMS]

/*
  INTERFACES
*/

export enum FieldStatus {
  New,
  Updated,
  Pushed,
}

interface OriginalEntityValue {
  category: EntityCategory | undefined
  party: EntityParty | undefined
}

export interface WorkItemRowState {
  pushed?: boolean
  closed?: boolean
  reopened?: boolean
  updated?: boolean
  skipClose?: boolean
  threadId?: string
  originalValues: OriginalEntityValue
  fieldStatuses?: Map<WorkItemPushFieldType, FieldStatus>
  fieldPushed: boolean
  highlightUpdated: boolean
}

type PushParams = Omit<WorkItemPushParams, "workItemId">
export type WorkItemRowAction =
  | { type: "Push"; params?: PushParams }
  | { type: "PushAll"; params?: PushParams; fields: WorkItemPushFieldType[] }
  | { type: "SetThreadId"; threadId: string | undefined }
  | { type: "Close"; ledgerThreadId: string | undefined }
  | { type: "Reopen" | "ClearFieldPushed" | "HighlightUpdatedFields" | "ClearHighlight" }
  | {
      type: "Update"
      entity: EntityParty | EntityCategory
    }
  | { type: "PushField"; params?: PushParams; field: WorkItemPushFieldType }
  | { type: "ResetField"; field: WorkItemPushFieldType }
  | {
      type: "UpdateField"
      entity: TypeaheadEntity
      previousEntity?: EntityCategory | EntityParty | null
      field: WorkItemPushFieldType
    }

/*
  REDUCER
*/
export function useWorkItemReducerWithActions(workItem: WorkItem) {
  const apolloClient = useApolloClient()
  const animationCallback = useAnimationCallback<HTMLDivElement>(
    React.useCallback(() => {
      apolloClient
        .refetchQueries({
          include: WORK_ITEM_STATE_CHANGE_REFETCH,
        })
        .then(() => evictWorkItem(apolloClient, workItem))
        .catch((e) => TrackJS?.track(e))
    }, [apolloClient, workItem]),
    [
      customKeyframes.fadeOut.getName(),
      customKeyframes.fadeOutLeft.getName(),
      customKeyframes.fadeOutRight.getName(),
    ]
  )

  const reducer = React.useReducer(useWorkItemReducer(workItem), {
    fieldPushed: false,
    highlightUpdated: false,
    originalValues: {
      category: undefined,
      party: undefined,
    },
  })

  return {
    reducer,
    animationCallback,
  }
}

function useWorkItemReducer(workItem: WorkItem) {
  const { legalEntityId, workItemId, ledgerType } = workItem
  const apolloClient = useApolloClient()
  const [pushQBO] = usePushWorkItemsMutation()
  const [pushDigits] = usePushDigitsWorkItemsMutation()
  const push = ledgerType === LedgerType.QuickBooks ? pushQBO : pushDigits
  const [close] = useCloseWorkItemsMutation()
  const [reopen] = useReopenWorkItemMutation()
  const viewKey = useViewVersion()

  return (curState: WorkItemRowState, action: WorkItemRowAction) => {
    const newState = { ...curState }

    switch (action.type) {
      case "Push":
        newState.pushed = true

        push({
          variables: { legalEntityId, workItems: { workItemId, ...action.params } },
        }).catch((e) => TrackJS?.track(e))

        return newState

      case "PushAll": {
        newState.updated = true
        newState.fieldPushed = true

        push({
          variables: { legalEntityId, workItems: { workItemId, ...action.params } },
        }).catch((e) => TrackJS?.track(e))

        const fieldStatuses = new Map(newState.fieldStatuses)

        action.fields.forEach((field) => {
          fieldStatuses.set(field, FieldStatus.Pushed)
        })
        newState.fieldStatuses = fieldStatuses

        return newState
      }

      case "PushField": {
        newState.updated = true
        newState.fieldPushed = true

        push({
          variables: { legalEntityId, workItems: { workItemId, ...action.params } },
        }).catch((e) => TrackJS?.track(e))

        const fieldStatuses = new Map(newState.fieldStatuses)
        fieldStatuses.set(action.field, FieldStatus.Pushed)
        newState.fieldStatuses = fieldStatuses

        return newState
      }

      case "SetThreadId":
        newState.threadId = action.threadId
        return newState

      case "Close": {
        newState.closed = true
        const ignoredFields = workItem.pushes
          .filter((p) => p.pushState === WorkItemPushState.Error)
          .map((p) => p.field)
        const now = dateTimeHelper.unixNowSeconds()
        close({
          variables: {
            legalEntityId,
            closedWorkItems: [{ workItemId, ignoredFields }],
            viewKey,
          },
          optimisticResponse: () => ({ closeWorkItems: {} }),
          update: (cache) => {
            if (curState.threadId && action.ledgerThreadId) {
              const id = cache.identify({ id: action.ledgerThreadId, __typename: "ThreadDetails" })
              cache.modify({
                id,
                fields: {
                  resolvedAt() {
                    return now
                  },
                },
                broadcast: true,
              })
            }

            if (curState.threadId) {
              const id = cache.identify({ id: curState.threadId, __typename: "ThreadDetails" })
              cache.modify({
                id,
                fields: {
                  resolvedAt() {
                    return now + 1 // must be resolved *after* the ledger thread
                  },
                },
                broadcast: true,
              })
            }
          },
          refetchQueries: REFETCH_ON_CLOSE,
        }).catch((e) => TrackJS?.track(e))

        return newState
      }

      case "Reopen":
        if (TRANSACTION_REVIEW_TYPES.includes(workItem.itemType)) {
          newState.reopened = true
        } else {
          // Boost uses the same animation for closing and reopening
          newState.closed = true
        }
        reopen({
          variables: { legalEntityId, workItemId },
        }).catch((e) => TrackJS?.track(e))

        return newState

      case "Update":
        newState.updated = true

        if (isPartyUpdate(action.entity)) {
          updateParty(apolloClient, workItem, action.entity)
        }
        if (isCategoryUpdate(action.entity)) {
          updateCategory(apolloClient, workItem, action.entity)
        }

        return newState

      case "UpdateField": {
        newState.updated = true

        switch (action.field) {
          case WorkItemPushFieldType.Category:
            if (isCategoryUpdate(action.entity)) {
              updateCategory(apolloClient, workItem, action.entity)
            }
            if (action.previousEntity && isCategoryUpdate(action.previousEntity)) {
              newState.originalValues.category = action.previousEntity
            }
            break
          case WorkItemPushFieldType.Party:
            if (isPartyUpdate(action.entity)) {
              updateParty(apolloClient, workItem, action.entity)
            } else if (isTypeaheadNewParty(action.entity)) {
              const party = createNewParty(action.entity)
              updateParty(apolloClient, workItem, party)
            }

            if (action.previousEntity && isPartyUpdate(action.previousEntity)) {
              newState.originalValues.party = action.previousEntity
            }
            break
        }

        const fieldStatuses = new Map(newState.fieldStatuses)
        fieldStatuses.set(action.field, FieldStatus.Updated)
        newState.fieldStatuses = fieldStatuses

        return newState
      }

      case "ClearFieldPushed":
        newState.fieldPushed = false
        return newState

      case "HighlightUpdatedFields":
        newState.highlightUpdated = true
        return newState

      case "ClearHighlight":
        newState.highlightUpdated = false
        return newState

      case "ResetField": {
        newState.updated = true

        switch (action.field) {
          case WorkItemPushFieldType.Category:
            if (newState.originalValues.category) {
              updateCategory(apolloClient, workItem, newState.originalValues.category)
            }
            break
          case WorkItemPushFieldType.Party:
            if (newState.originalValues.party) {
              updateParty(apolloClient, workItem, newState.originalValues.party)
            } else {
              removeUserProvidedForField(apolloClient, workItem, action.field)
            }
            break
        }

        const fieldStatuses = new Map(newState.fieldStatuses)
        fieldStatuses.set(action.field, FieldStatus.New)
        newState.fieldStatuses = fieldStatuses

        return newState
      }
    }
  }
}

function isPartyUpdate(entity: TypeaheadEntity): entity is EntityParty {
  return !!(entity as EntityParty).designation
}

function isCategoryUpdate(entity: TypeaheadEntity): entity is EntityCategory {
  return !!(entity as EntityCategory).type
}

function createNewParty(entity: NewPartyEntity): EntityParty {
  return {
    name: entity.name,
    id: entity.id || NEW_ENTITY_ID,
    designation: PartyDesignation.UnknownDesignation,
    shortDescription: null,
    iconUrl: null,
    roles: null,
  }
}

function updateParty(
  { cache }: ApolloClient<object>,
  workItem: WorkItem,
  entityParty: EntityParty
) {
  const { object } = getSupportingTransactions(workItem)
  const id = cache.identify(workItem)
  if (!object) return

  const isTransactionReview = TRANSACTION_REVIEW_TYPES.includes(workItem.itemType)

  // Ensure the party has the __typename property, or it won't write to the cache
  const party = {
    ...entityParty,
    __typename: "EntityParty" as const,
  }

  cache.modify({
    id,
    fields: {
      transactions(current: WorkItemTransaction[]) {
        const newList = current.filter((t) => t.supportType !== WorkItemSupportType.UserProvided)
        const userProvidedTxn = current.find(
          (t) => t.supportType === WorkItemSupportType.UserProvided
        )
        const newTransaction: WorkItemTransaction = userProvidedTxn
          ? { ...userProvidedTxn, party }
          : isTransactionReview
            ? {
                ...object,
                supportType: WorkItemSupportType.UserProvided,
                party,
                // Ensure we don't update the category, so we can continue to show the suggestion if applicable
                displayCategory: null,
                splitCategory: null,
                creditValue: null,
                debitValue: null,
                displayValue: null,
              }
            : {
                ...object,
                supportType: WorkItemSupportType.UserProvided,
                party,
              }
        return [...newList, newTransaction]
      },
    },
    broadcast: true,
  })
}

function removeUserProvidedForField(
  { cache }: ApolloClient<object>,
  workItem: WorkItem,
  field: WorkItemPushFieldType
) {
  const id = cache.identify(workItem)

  cache.modify({
    id,
    fields: {
      transactions(current: WorkItemTransaction[]) {
        const newList = current.filter((t) => t.supportType !== WorkItemSupportType.UserProvided)

        const userProvidedTxn = current.find(
          (t) => t.supportType === WorkItemSupportType.UserProvided
        )

        if (userProvidedTxn) {
          switch (field) {
            case WorkItemPushFieldType.Party:
              return [...newList, { ...userProvidedTxn, party: null }]
            default:
              return [...newList, userProvidedTxn]
          }
        }

        return [...newList]
      },
    },
    broadcast: true,
  })
}

function updateCategory(
  { cache }: ApolloClient<object>,
  workItem: WorkItem,
  category: EntityCategory | Category
) {
  const { from } = getSupportingTransactions(workItem)
  const id = cache.identify(workItem)
  if (!from) return

  cache.modify({
    id,
    fields: {
      transactions(current: WorkItemTransaction[]) {
        const newList = current.filter((t) => t.supportType !== WorkItemSupportType.UserProvided)
        const userProvidedTxn = current.find(
          (t) => t.supportType === WorkItemSupportType.UserProvided
        )
        const newTransaction: WorkItemTransaction = userProvidedTxn
          ? {
              ...userProvidedTxn,
              displayCategory: null,
              splitCategory: null,
            }
          : {
              ...from,
              displayCategory: null,
              splitCategory: null,
              supportType: WorkItemSupportType.UserProvided,
            }

        const displayNumber = isCategory(category)
          ? category.displayNumber
          : category.chartOfAccountsDisplayNumber

        newTransaction.displayCategory = {
          __typename: "EntityCategory",
          id: category.id,
          name: category.name,
          displayKey: category.displayKey,
          chartOfAccountsDisplayNumber: displayNumber,
          type: category.type,
          subtype: category.subtype,
          parentCategoryId: category.parentCategoryId,
          productArea: category.productArea,
          active: category.active,
        } as EntityCategory

        return [...newList, newTransaction]
      },
    },
    broadcast: true,
  })
}

function evictWorkItem({ cache }: ApolloClient<object>, workItem: WorkItem) {
  // evict removes the work item from the cache and gc removes any dangling
  // references to it, including if it is present in any cached ListWorkItems queries.
  const id = cache.identify(workItem)
  cache.evict({ id })
  cache.gc()
}
